import React, { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { showError, showSuccess } from "../components/ToastNotifications";
import AuthShowcase, { AuthStatus, SubmitButton, TextField, useCooldown, validateEmail } from "../components/auth/AuthShowcase";

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const emailInput = useRef(null);
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [fieldError, setFieldError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");
  const [cooldown, startCooldown] = useCooldown(60);

  const sendLink = async (address) => {
    setSubmitting(true);
    setError("");
    try {
      // The API answers the same way whether or not an account exists, so this never reveals who is registered.
      const result = await apiRequest("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email: address }) });
      setSentTo(address);
      startCooldown();
      showSuccess(result.message || "Check your inbox for a reset link.");
    } catch (requestError) {
      setError(requestError.message);
      showError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (event) => {
    event.preventDefault();
    const problem = validateEmail(email);
    setFieldError(problem);
    if (problem) { emailInput.current?.focus(); return; }
    sendLink(email.trim());
  };

  return (
    <AuthShowcase
      eyebrow="Account recovery"
      title={<>Locked out? <em>Let's get you back in.</em></>}
      intro="We'll email you a secure, single-use link to choose a new password for your Socialyorix workspace."
    >
      {sentTo ? <div className="auth-state">
        <AuthStatus tone="info" icon="ti-mail-opened" />
        <h2>Check your inbox</h2>
        <p role="status">If an account exists for <strong>{sentTo}</strong>, a password reset link is on its way. It works once and expires soon.</p>
        <ul className="auth-tips">
          <li><i className="ti ti-folder" aria-hidden="true" /> Not there? Check spam or promotions.</li>
          <li><i className="ti ti-shield-lock" aria-hidden="true" /> Only the newest link will work.</li>
        </ul>
        {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
        <button type="button" className="auth-submit is-secondary" disabled={submitting || cooldown > 0} onClick={() => sendLink(sentTo)}>
          {submitting ? <><span className="auth-spinner" aria-hidden="true" /> Sending…</> : cooldown > 0 ? <><i className="ti ti-clock" aria-hidden="true" /> Resend in {cooldown}s</> : <><i className="ti ti-mail-forward" aria-hidden="true" /> Resend link</>}
        </button>
        <p className="auth-switch"><button type="button" className="auth-link-button" onClick={() => { setSentTo(""); setError(""); }}>Use a different email</button> · <Link className="auth-link" to="/login">Back to sign in</Link></p>
      </div> : <>
        <header className="auth-card-head">
          <h2>Forgot your password?</h2>
          <p>Enter the email you use to sign in and we'll send you a reset link.</p>
        </header>
        <form className="auth-form" onSubmit={submit} noValidate>
          {error && <div className="auth-alert" role="alert"><i className="ti ti-alert-circle" aria-hidden="true" /><span>{error}</span></div>}
          <TextField id="forgot-password-email" label="Email address" icon="ti-mail" type="email" autoComplete="email" maxLength={254} placeholder="you@business.com"
            ref={emailInput} value={email} error={fieldError} autoFocus
            onChange={(event) => { setEmail(event.target.value); if (fieldError) setFieldError(""); }}
            onBlur={() => email && setFieldError(validateEmail(email))} required />
          <SubmitButton busy={submitting} busyLabel="Sending link…">Send reset link</SubmitButton>
        </form>
        <p className="auth-switch">Remembered it? <Link className="auth-link" to="/login">Back to sign in</Link></p>
      </>}
    </AuthShowcase>
  );
};

export default ForgotPassword;
